import {
  DeleteSweep as ClearAllIcon,
  Delete as DeleteIcon,
  Cancel as InvalidIcon,
  CheckCircle as ValidIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useHistory } from "../hooks";

export function HistoryPage() {
  const { history, persistenceEnabled, clearHistory, removeFromHistory } =
    useHistory();
  const navigate = useNavigate();

  // Log the persistence feature toggle state when visiting the History tab
  useEffect(() => {
    console.log(
      "[History Feature Toggle] persistenceEnabled:",
      persistenceEnabled,
    );
  }, [persistenceEnabled]);

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    }).format(date);
  };

  if (history.length === 0) {
    return (
      <Box>
        <Typography variant="h4" component="h2" gutterBottom>
          Validation History
        </Typography>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No validation history yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {persistenceEnabled
              ? "Validated email addresses will appear here. History is stored in this browser and persists across refreshes."
              : "Validated email addresses will appear here. History is stored in memory and will be lost on page refresh."}
          </Typography>
          <Button variant="contained" onClick={() => navigate("/")}>
            Validate an Email
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" component="h2">
            Validation History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {history.length} email{history.length !== 1 ? "s" : ""} validated
            {persistenceEnabled ? " in this browser" : " this session"}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="error"
          startIcon={<ClearAllIcon />}
          onClick={clearHistory}
        >
          Clear All
        </Button>
      </Box>

      <Paper>
        <List disablePadding>
          {history.map((item, index) => (
            <Box key={item.id}>
              {index > 0 && <Divider />}
              <ListItem
                sx={{
                  py: 2,
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                  {item.result.isValid ? (
                    <ValidIcon color="success" />
                  ) : (
                    <InvalidIcon color="error" />
                  )}
                </Box>
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        wordBreak: "break-all",
                      }}
                    >
                      {item.email}
                    </Typography>
                  }
                  secondary={
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        mt: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        label={item.result.isValid ? "Valid" : "Invalid"}
                        color={item.result.isValid ? "success" : "error"}
                        size="small"
                      />
                      {item.result.detectedProvider && (
                        <Chip
                          label={item.result.detectedProvider.displayName}
                          variant="outlined"
                          size="small"
                        />
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ alignSelf: "center" }}
                      >
                        {formatTime(item.timestamp)}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="Remove from history"
                    onClick={() => removeFromHistory(item.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </Box>
          ))}
        </List>
      </Paper>
    </Box>
  );
}
